import type { Feature, Polygon, Point } from 'geojson';
import type { RogoShapefile } from '../types/types';
import type shp from 'shpjs';

/**
 * Service responsible for parsing CSV files into GeoJSON FeatureCollections
 *
 * Features:
 * - Smart header detection (handles CSV with or without headers)
 * - Intelligent coordinate column detection (by name and value range)
 * - Support for multiple column orders (lat,lon or lon,lat, with optional sample_id)
 * - Automatic polygon closing for boundary CSV files
 * - Preserves additional columns as feature properties
 *
 * Architecture:
 * - Implemented as a singleton for easy dependency injection
 * - Uses @rogoag/shared-ts CSV utilities for parsing
 *
 * Usage:
 * ```typescript
 * const service = CSVParserService.getInstance();
 * const result = await service.parseCSV(csvFile);
 * ```
 */
export class CsvParserService {
  private static instance: CsvParserService;

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get the singleton instance
   */
  static getInstance(): CsvParserService {
    if (!CsvParserService.instance) {
      CsvParserService.instance = new CsvParserService();
    }

    return CsvParserService.instance;
  }

  /**
   * Parse CSV file to GeoJSON FeatureCollection
   *
   * Intelligently detects column format and creates appropriate geometries:
   * - For samples (pts): Creates Point features with sample_id in properties
   * - For boundaries (bnd): Creates a Polygon feature from coordinate list
   *
   * Supports multiple column order formats:
   * - lat,lon,sample_id or lon,lat,sample_id or sample_id,lat,lon or sample_id,lon,lat
   * - lat,lon or lon,lat (for boundaries)
   * - Automatically detects coordinate order based on column names and value ranges
   * - Handles CSV files with or without headers
   *
   * @param file The CSV file to convert
   * @returns GeoJSON FeatureCollection with filename
   */
  async parseCSV(file: RogoShapefile): Promise<shp.FeatureCollectionWithFilename> {
    const { CSV } = await import('@rogoag/shared-ts');

    // Convert ArrayBuffer to string
    const decoder = new TextDecoder('utf-8');
    const csvContent = decoder.decode(file.data);

    // Detect if CSV has headers by checking if first row values are numeric (likely data, not headers)
    const lines = csvContent.trim().split('\n');

    if (lines.length === 0) {
      throw new Error('CSV file is empty');
    }

    const firstRowValues = lines[0].split(',').map((v) => v.trim());
    const hasHeaders = !this.isLikelyDataRow(firstRowValues);

    let rows: Record<string, string>[];
    let columns: string[];

    if (hasHeaders) {
      // Parse CSV normally with headers
      rows = CSV.convertCSVtoJSON(csvContent);

      if (rows.length === 0) {
        throw new Error('CSV file is empty or has no data rows');
      }

      columns = Object.keys(rows[0]);
    } else {
      // Parse CSV as data array, then create synthetic column names
      const dataArray = CSV.convertCSVtoDataArray(csvContent);

      if (dataArray.length === 0) {
        throw new Error('CSV file is empty');
      }

      // Create synthetic column names: col_0, col_1, col_2, etc.
      const numColumns = dataArray[0].length;
      columns = Array.from({ length: numColumns }, (_, i) => `col_${i}`);

      // Convert data array to rows with synthetic column names
      rows = dataArray.map((rowArray) => {
        const row: Record<string, string> = {};
        columns.forEach((col, i) => {
          row[col] = rowArray[i] || '';
        });

        return row;
      });
    }

    // Detect coordinate columns and their order
    const coordInfo = this.detectCoordinateColumns(columns, rows);

    if (!coordInfo) {
      throw new Error(
        'Could not detect coordinate columns in CSV. Expected columns like: lat/lon, latitude/longitude, or numeric columns with coordinate-like values',
      );
    }

    const { latColumn, lonColumn, otherColumns } = coordInfo;

    // Create features based on mode
    if (file.mode === 'bnd') {
      return this.createBoundaryFeatureCollection(rows, latColumn, lonColumn, file.name);
    } else {
      return this.createPointFeatureCollection(rows, latColumn, lonColumn, otherColumns, file.name);
    }
  }

  /**
   * Create a Polygon FeatureCollection for boundary data
   * @private
   */
  private createBoundaryFeatureCollection(
    rows: Record<string, string>[],
    latColumn: string,
    lonColumn: string,
    filename: string,
  ): shp.FeatureCollectionWithFilename {
    const coordinates: [number, number][] = rows.map((row) => {
      const lat = parseFloat(row[latColumn]);
      const lon = parseFloat(row[lonColumn]);

      return [lon, lat]; // GeoJSON uses [lon, lat] order
    });

    // Auto-close the polygon if not already closed
    const firstCoord = coordinates[0];
    const lastCoord = coordinates[coordinates.length - 1];

    if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
      coordinates.push([...firstCoord]);
    }

    const feature: Feature<Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
      properties: {},
    };

    return {
      type: 'FeatureCollection',
      features: [feature],
      fileName:
        filename
          .split('/')
          .pop()
          ?.replace(/\.csv$/i, '') || 'boundary',
    };
  }

  /**
   * Create a Point FeatureCollection for sample/point data
   * @private
   */
  private createPointFeatureCollection(
    rows: Record<string, string>[],
    latColumn: string,
    lonColumn: string,
    otherColumns: string[],
    filename: string,
  ): shp.FeatureCollectionWithFilename {
    const features: Feature<Point>[] = rows.map((row) => {
      const lat = parseFloat(row[latColumn]);
      const lon = parseFloat(row[lonColumn]);

      // Build properties from all other columns
      const properties: Record<string, any> = {};
      otherColumns.forEach((col) => {
        properties[col] = row[col];
      });

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lon, lat], // GeoJSON uses [lon, lat] order
        },
        properties,
      };
    });

    return {
      type: 'FeatureCollection',
      features,
      fileName:
        filename
          .split('/')
          .pop()
          ?.replace(/\.csv$/i, '') || 'points',
    };
  }

  /**
   * Check if a row of values is likely data (not headers)
   *
   * A row is considered data if most/all values are numeric (coordinates)
   * Headers would typically have text like "lat", "lon", "sample_id", etc.
   *
   * @param values Array of string values from a CSV row
   * @returns true if the row appears to be data, false if it appears to be headers
   * @private
   */
  private isLikelyDataRow(values: string[]): boolean {
    // If we have 2-3 columns, check if at least 2 are numeric
    // (2 for coordinates, possibly 1 non-numeric for sample ID)
    let numericCount = 0;

    for (const value of values) {
      const trimmed = value.trim();
      const asNumber = parseFloat(trimmed);

      if (!isNaN(asNumber) && trimmed !== '') {
        numericCount++;
      }
    }

    // If at least 2 values are numeric, it's likely a data row
    // (since we need at least lat and lon)
    return numericCount >= 2;
  }

  /**
   * Detect coordinate columns in CSV data
   *
   * Uses heuristics to identify lat/lon columns:
   * 1. Check for common column names (lat, lon, latitude, longitude, etc.)
   * 2. Check value ranges (lat: -90 to 90, lon: -180 to 180)
   * 3. For ambiguous cases, assume smaller absolute values are latitude
   *
   * @param columns Array of column names
   * @param rows Array of data rows
   * @returns Coordinate column information or null if not detected
   * @private
   */
  private detectCoordinateColumns(
    columns: string[],
    rows: Record<string, string>[],
  ): { latColumn: string; lonColumn: string; otherColumns: string[] } | null {
    // Common column name patterns
    const latPatterns = /^(lat|latitude|y)$/i;
    const lonPatterns = /^(lon|lng|long|longitude|x)$/i;

    // Try to find by column name first
    let latColumn = columns.find((col) => latPatterns.test(col));
    let lonColumn = columns.find((col) => lonPatterns.test(col));

    if (latColumn && lonColumn) {
      const otherColumns = columns.filter((col) => col !== latColumn && col !== lonColumn);

      return { latColumn, lonColumn, otherColumns };
    }

    // If not found by name, try to detect by value ranges
    // Look for numeric columns
    const numericColumns = columns.filter((col) => {
      // Check if first few values are numeric
      const testRows = rows.slice(0, Math.min(5, rows.length));

      return testRows.every((row) => {
        const val = parseFloat(row[col]);

        return !isNaN(val);
      });
    });

    if (numericColumns.length < 2) {
      return null; // Need at least 2 numeric columns for coordinates
    }

    // Try to identify lat/lon by value ranges
    // Sample a few values to check ranges
    const testRows = rows.slice(0, Math.min(10, rows.length));

    for (let i = 0; i < numericColumns.length - 1; i++) {
      for (let j = i + 1; j < numericColumns.length; j++) {
        const col1 = numericColumns[i];
        const col2 = numericColumns[j];

        // Check if values fall in lat/lon ranges
        const col1Values = testRows.map((row) => Math.abs(parseFloat(row[col1])));
        const col2Values = testRows.map((row) => Math.abs(parseFloat(row[col2])));

        const col1Max = Math.max(...col1Values);
        const col2Max = Math.max(...col2Values);

        // Latitude is typically -90 to 90, longitude -180 to 180
        // Assume column with smaller max absolute value is latitude
        if (col1Max <= 90 && col2Max <= 180) {
          if (col1Max < col2Max) {
            // col1 is likely lat, col2 is likely lon
            latColumn = col1;
            lonColumn = col2;
          } else {
            // col2 is likely lat, col1 is likely lon
            latColumn = col2;
            lonColumn = col1;
          }

          const otherColumns = columns.filter((col) => col !== latColumn && col !== lonColumn);

          return { latColumn, lonColumn, otherColumns };
        }
      }
    }

    return null; // Could not detect coordinate columns
  }
}
